import type {LabelGeometry} from './label-sizes'
import type {LabelSubstanceHeadingSize} from './types'

/** one text size of the compact label in label px; the label steps down until its content fits (CY-421) */
export interface LabelFitStep {
  step: 1 | 2 | 3 | 4 | 5
  body: number
  heading: number
  patientName: number
  gap: number
}

export const FIT_STEPS: readonly LabelFitStep[] = [
  {step: 1, body: 20, heading: 32, patientName: 32, gap: 8},
  {step: 2, body: 19, heading: 30, patientName: 32, gap: 7},
  {step: 3, body: 18, heading: 30, patientName: 30, gap: 7},
  {step: 4, body: 17, heading: 28, patientName: 28, gap: 6},
  {step: 5, body: 16, heading: 26, patientName: 26, gap: 6},
]

/** the twoLines and twoLinesSmall headings scale with the regular one, as 24 and 20 do to 32 */
export const HEADING_SCALE: Record<LabelSubstanceHeadingSize, number> = {
  regular: 1,
  twoLines: 0.75,
  twoLinesSmall: 0.625,
}

/** captions, chips, the Produced and Batches lines and the pharmacy line stay at this size on every step */
export const SMALL_TEXT_PX = 16

const CHIP_EXTRA = 18
const CHIP_GAP = 6
const CHIP_ROW = 28
const ROUTE_LINE = 19
const ROUTE_PADDING = 16
const DIVIDER = 2
const LINEAR_BARCODE = 48

/**
 * react-pdf cannot measure text, so the width is estimated per character: a space is narrow in Geist and
 * Satoshi, the rest is about 0.56 em regular and 0.6 em bold
 */
export function estimateTextWidth(text: string, fontPx: number, bold = false) {
  const spaces = text.split(' ').length - 1
  return ((text.length - spaces) * (bold ? 0.6 : 0.56) + spaces * 0.28) * fontPx
}

/** the parts on one line when they fit, else one part per line; a line is never broken inside a part */
export function splitLine(
  parts: readonly (string | null | undefined)[],
  separator: string,
  fontPx: number,
  widthPx: number,
  bold = false
): string[] {
  const present = parts.filter((part): part is string => Boolean(part))
  if (!present.length) return []
  const joined = present.join(separator)
  return estimateTextWidth(joined, fontPx, bold) <= widthPx ? [joined] : present
}

/** what the compact label prints, as the parts of each line before they are split */
export interface CompactLabelContent {
  patientName: string[]
  patientDetails: string[]
  ward: string[]
  headings: string[]
  headingSize: LabelSubstanceHeadingSize
  products: string[][]
  carrier: string[]
  volume: string[]
  actualLines: string[]
  routeWarning: string | null
  chips: string[]
  produced: string[]
  hasBatches: boolean
  hasPharmacy: boolean
  hasLinearBarcode: boolean
}

export interface LabelFit {
  step: LabelFitStep
  lines: {
    patientName: string[]
    patientDetails: string[]
    ward: string[]
    products: string[]
    carrier: string[]
    volume: string[]
    produced: string[]
    chips: string[]
  }
}

interface Separators {
  field: string
  label: string
}

function resolveLines(
  content: CompactLabelContent,
  step: LabelFitStep,
  geometry: LabelGeometry,
  separators: Separators
) {
  const {patientColumn, bodyWidth} = geometry
  return {
    patientName: splitLine(content.patientName, ' ', step.patientName, patientColumn, true),
    patientDetails: splitLine(content.patientDetails, separators.field, step.body, patientColumn),
    ward: splitLine(content.ward, separators.field, step.body, patientColumn),
    products: content.products.flatMap(parts => splitLine(parts, separators.label, step.body, bodyWidth)),
    carrier: splitLine(content.carrier, separators.label, step.body, bodyWidth),
    volume: splitLine(content.volume, separators.label, step.body, bodyWidth),
    produced: splitLine(content.produced, separators.label, SMALL_TEXT_PX, bodyWidth),
    chips: content.chips,
  }
}

const lineHeight = (fontPx: number) => fontPx + 4

function chipRows(chips: readonly string[], widthPx: number) {
  let rows = 0
  let used = 0
  for (const chip of chips) {
    const width = estimateTextWidth(chip, SMALL_TEXT_PX, true) + CHIP_EXTRA
    if (rows && used + CHIP_GAP + width <= widthPx) {
      used += CHIP_GAP + width
    } else {
      rows += 1
      used = width
    }
  }
  return rows
}

function estimateHeight(
  content: CompactLabelContent,
  lines: LabelFit['lines'],
  step: LabelFitStep,
  geometry: LabelGeometry
) {
  const {bodyWidth, dataMatrix} = geometry

  const patient =
    lines.patientName.length * lineHeight(step.patientName) +
    (lines.patientDetails.length + lines.ward.length) * lineHeight(step.body)

  const headingPx = step.heading * HEADING_SCALE[content.headingSize]
  const headingLines = content.headings.reduce(
    (sum, heading) => sum + (estimateTextWidth(heading, headingPx, true) <= bodyWidth ? 1 : 2),
    0
  )
  const bodyLines = lines.products.length + lines.carrier.length + lines.volume.length + content.actualLines.length
  // the Administration line is body + 3
  const substance = headingLines * lineHeight(headingPx) + bodyLines * lineHeight(step.body) + step.body + 7

  const rows = chipRows(lines.chips, bodyWidth)
  const routeLines = content.routeWarning
    ? estimateTextWidth(content.routeWarning, SMALL_TEXT_PX, true) <= bodyWidth - ROUTE_PADDING
      ? 1
      : 2
    : 0

  const blocks = [
    Math.max(dataMatrix, patient),
    DIVIDER,
    substance,
    DIVIDER,
    routeLines ? routeLines * ROUTE_LINE + 6 : null,
    rows ? rows * CHIP_ROW + CHIP_GAP * (rows - 1) : null,
    // caption over the Use by value, which is body + 5
    18 + step.body + 9,
    lines.produced.length ? lines.produced.length * 20 : null,
    content.hasBatches ? 20 : null,
    content.hasPharmacy ? 25 : null,
  ].filter((height): height is number => height !== null)

  const padding = geometry.body.paddingTop + geometry.body.paddingBottom
  const total = blocks.reduce((sum, height) => sum + height, 0)
  return padding + total + step.gap * (blocks.length - 1) + (content.hasLinearBarcode ? LINEAR_BARCODE : 0)
}

/** the estimated content height in label px at one step */
export function estimateCompactHeight(
  content: CompactLabelContent,
  step: LabelFitStep,
  geometry: LabelGeometry,
  separators: Separators
) {
  return estimateHeight(content, resolveLines(content, step, geometry, separators), step, geometry)
}

/**
 * The first step whose content fits the label height; a label still too tall at the last step prints there with
 * every field, nothing is left out (CY-421)
 */
export function fitCompactLabel(
  content: CompactLabelContent,
  geometry: LabelGeometry,
  separators: Separators
): LabelFit {
  const last = FIT_STEPS[FIT_STEPS.length - 1]
  for (const step of FIT_STEPS) {
    const lines = resolveLines(content, step, geometry, separators)
    if (estimateHeight(content, lines, step, geometry) <= geometry.page.height || step === last) return {step, lines}
  }
  throw new Error('FIT_STEPS is empty')
}
